import { FormControl, Link, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import type { FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../assets/styles/themes/tokens';
import { emailAddresses } from '../../constants';
import type { ChangeEmailPayload } from '../ChangeEmailSection/ChangeEmailSection';
import LabelledTextField from '../LabelledTextField/LabelledTextField';
import SettingItem from '../SettingItem';

interface EmailFieldsRendererProps {
	isEditMode: boolean;
	userEmail: string;
	canEditEmail: boolean;
	register: () => void;
	triggerValidation: (
		payload?: keyof ChangeEmailPayload | (keyof ChangeEmailPayload)[] | undefined
	) => Promise<boolean>;
	errors: FieldErrors<ChangeEmailPayload>;
}

const EmailFieldsRenderer = ({
	isEditMode,
	userEmail,
	canEditEmail,
	register,
	triggerValidation,
	errors,
}: EmailFieldsRendererProps) => {
	const { t } = useTranslation();

	if (!isEditMode) {
		return (
			<SettingItem data-testid="email-input-enabled" title={t('E-Mail-Address')}>
				<Typography>{userEmail}</Typography>
			</SettingItem>
		);
	}

	return canEditEmail ? (
		<Grid
			data-testid="email-fields-renderer"
			container
			direction="column"
			sx={{
				borderRadius: borderRadius.lg,
				border: `1px solid ${palette.elevation.outlined}`,
				padding: spaces.md,
				backgroundColor: palette.background.paperElevation2,
				gap: spaces.xs,
			}}
		>
			<FormControl fullWidth sx={{ gap: spaces.md }}>
				<LabelledTextField
					dataTestId="email-input"
					name="email"
					label={t('E-Mail-Address *')}
					placeholder={t('eg max.mustermann@beispiel.de')}
					inputRef={register}
					error={errors.email}
				/>
				<LabelledTextField
					dataTestId="current-password-input"
					name="password"
					label={t('Confirm with current password *')}
					placeholder={t('Enter your password')}
					isPasswordTextField
					inputRef={register}
					onChange={() => triggerValidation('email')}
					error={errors.password}
				/>
			</FormControl>
		</Grid>
	) : (
		<SettingItem title={t('E-Mail-Address *')}>
			<TextField
				data-testid="email-input-disabled"
				value={userEmail}
				type={'text'}
				variant="outlined"
				error={true}
				disabled={true}
				inputProps={{
					style: {
						paddingTop: spaces.xs,
						paddingBottom: spaces.xs,
						paddingLeft: spaces.sm,
						paddingRight: spaces.sm,
					},
				}}
				sx={{
					width: '100%',
					'& .MuiOutlinedInput-root': {
						paddingRight: spaces.sm,
					},
				}}
				InputProps={{
					sx: {
						border: `1px solid ${palette.components.input.outlined.enabledBorder}`,
						borderRadius: borderRadius.sm,
						paddingRight: 0,
					},
				}}
			/>
			<Typography
				sx={{
					padding: '3px 14px 0px 14px',
					color: palette.text.disabled,
					fontSize: '12px',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '20px',
					letterSpacing: '0.4px',
				}}
			>
				{t(
					'Your email address is no longer active or has changed? ' +
						'Please write us a message in the chat or to email support'
				)}
				<Link href={`mailto:${emailAddresses.support}`} underline="none">
					{emailAddresses.support}
				</Link>
			</Typography>
		</SettingItem>
	);
};

export default EmailFieldsRenderer;
